import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Table,
    Badge,
    Button,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Pagination,
    PaginationItem,
    PaginationLink,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader.js";
import CustomDropdown from "components/Common/CustomDropdown";
import CustomPagination from "components/Common/CustomPagination";
import Swal from "sweetalert2";
import {
    getCategories,
    addCategory,
    updateCategory,
    deleteCategory,
    toggleCategoryStatus,
} from "../../api/ApiServices";
import {
    FaEdit,
    FaEllipsisV,
    FaPause,
    FaPlay,
    FaTrash,
} from "react-icons/fa";
const ITEMS_PER_PAGE = 10;

const Categories = () => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);

    const [formData, setFormData] = useState({
        name: "",
    });



    const [validationErrors, setValidationErrors] = useState({
        name: "",
    });
    const [touched, setTouched] = useState({
        name: false,
    });

    /* =====================================================
       FETCH CATEGORIES
    ===================================================== */

    const fetchCategories = async (page = 1, search = "") => {
        setLoading(true);

        try {
            const res = await getCategories({
                search,
                page,
                limit: ITEMS_PER_PAGE,
            });

            if (res.success) {
                setCategories(res.data || []);
                setTotalItems(res.pagination?.totalItems || 0);
                setTotalPages(res.pagination?.totalPages || 1);
                setCurrentPage(res.pagination?.currentPage || 1);
            } else {
                setCategories(res.data || []);
                setTotalItems(res.total || 0);
                setTotalPages(res.totalPages || 1);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);

            Swal.fire({
                title: "Error",
                text: "Failed to load categories",
                icon: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories(1, searchTerm);
    }, [searchTerm]);

    /* =====================================================
   VALIDATION FUNCTIONS
===================================================== */

    const validateName = (name) => {
        const trimmedName = name?.trim() || "";

        if (!trimmedName) {
            return "Category name is required";
        }
        if (trimmedName.length < 2) {
            return "Name must be at least 2 characters long";
        }
        if (trimmedName.length > 20) {
            return "Name cannot exceed 20 characters";
        }
        return "";
    };

    const validateForm = () => {
        const nameError = validateName(formData.name);
        setValidationErrors({ name: nameError });
        setTouched({ name: true });
        return !nameError;
    };

    const handleFieldBlur = (field) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        if (field === "name") {
            const error = validateName(formData.name);
            setValidationErrors((prev) => ({ ...prev, name: error }));
        }
    };

    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (field === "name") {
            const error = validateName(value);
            setValidationErrors((prev) => ({ ...prev, name: error }));
        }
    };

    const isFormValid = () => {
        return !validateName(formData.name);
    };

    /* =====================================================
       PAGINATION
    ===================================================== */

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            fetchCategories(page, searchTerm);
        }
    };

    /* =====================================================
       MODAL
    ===================================================== */

    const openCreateModal = () => {
        setEditMode(false);
        setEditingId(null);
        setFormData({ name: "" });
        setValidationErrors({ name: "" });
        setTouched({ name: false });
        setModalOpen(true);
    };


    const openEditModal = (item) => {
        setEditMode(true);
        setEditingId(item.uuid || item._id);
        setFormData({ name: item.name || "" });
        setValidationErrors({ name: "" });
        setTouched({ name: false });
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setFormData({ name: "" });
        setValidationErrors({ name: "" });
        setTouched({ name: false });
    };

    /* =====================================================
       SUBMIT
    ===================================================== */

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            const firstErrorField = document.querySelector(".is-invalid");
            if (firstErrorField) {
                firstErrorField.focus();
            }
            return;
        }

        try {
            const payload = {
                name: formData.name.trim(),
            };

            if (editMode) {
                const res = await updateCategory(editingId, payload);
                if (res.success) {
                    Swal.fire({
                        title: "Success",
                        text: "Category updated successfully",
                        icon: "success",
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    fetchCategories(currentPage, searchTerm);
                }
            } else {
                const res = await addCategory(payload);
                if (res.success) {
                    Swal.fire({
                        title: "Success",
                        text: "Category created successfully",
                        icon: "success",
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    fetchCategories(1, searchTerm);
                }
            }

            closeModal();
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Failed to save category";

            if (errorMessage.toLowerCase().includes("duplicate") ||
                errorMessage.toLowerCase().includes("already exists")) {
                setValidationErrors((prev) => ({
                    ...prev,
                    name: "Category name already exists",
                }));
                setTouched((prev) => ({
                    ...prev,
                    name: true,
                }));
                return;
            }

            Swal.fire({
                title: "Error",
                text: errorMessage,
                icon: "error",
            });
        }
    };

    /* =====================================================
       STATUS
    ===================================================== */

    const handleToggleStatus = async (id) => {
        try {
            const res = await toggleCategoryStatus(id);

            if (res.success) {
                setCategories((prev) =>
                    prev.map((item) =>
                        item.uuid === id || item._id === id
                            ? {
                                ...item,
                                isActive: !item.isActive,
                            }
                            : item
                    )
                );

                Swal.fire({
                    title: "Status Updated",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false,
                });
            }
        } catch (error) {
            Swal.fire(
                "Error",
                "Failed to update status",
                "error"
            );
        }
    };

    /* =====================================================
       DELETE
    ===================================================== */

    const handleDelete = async (id) => {
        const confirm = await Swal.fire({
            title: "Delete Category?",
            text: "This action cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f5365c",
            confirmButtonText: "Yes, delete",
            cancelButtonText: "Cancel",
        });

        if (!confirm.isConfirmed) return;

        try {
            const res = await deleteCategory(id);

            if (res.success) {
                Swal.fire({
                    title: "Deleted!",
                    text: "Category deleted successfully",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });

                fetchCategories(currentPage, searchTerm);
            }
        } catch (error) {
            Swal.fire(
                "Error",
                "Failed to delete category",
                "error"
            );
        }
    };

    /* =====================================================
       STATUS FILTER
    ===================================================== */
    const [statusFilter, setStatusFilter] = useState("All");

    const filteredCategories = categories.filter((item) => {
        if (statusFilter === "Active") return item.isActive === true;
        if (statusFilter === "Inactive") return item.isActive === false;
        return true;
    });

    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <>
            <CommonHeader />

            <Container className="mt--7" fluid>
                <Row>
                    <Col>
                        <Card className="shadow border-0">
                            <CardHeader className="bg-white border-0 pb-0 pt-4">
                                <Row className="align-items-center">
                                    <Col>
                                        <h3 className="mb-0 font-weight-bold text-dark">
                                            Category Management
                                        </h3>
                                    </Col>
                                    <Col className="text-right">
                                        <Button
                                            size="sm"
                                            onClick={openCreateModal}
                                            style={{
                                                color: "var(--background-white)",
                                            }}
                                            className="bg-button-gradient-primary"
                                        >
                                            + Add Category
                                        </Button>
                                    </Col>
                                </Row>
                            </CardHeader>

                            {/* ================= BODY ================= */}
                            <CardBody>
                                {/* SEARCH & FILTER */}
                                <Row className="align-items-start align-items-lg-center mb-3 px-3 flex-column flex-lg-row" style={{ gap: "12px" }}>
                                    <Col lg="5" className="px-0">
                                        <InputGroup>
                                            <InputGroupAddon addonType="prepend">
                                                <InputGroupText>
                                                    <i className="fas fa-search" />
                                                </InputGroupText>
                                            </InputGroupAddon>

                                            <Input
                                                type="search"
                                                placeholder="Search categories..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </InputGroup>
                                    </Col>

                                    <Col lg="3" className="px-0">
                                        <CustomDropdown
                                            value={statusFilter}
                                            onChange={setStatusFilter}
                                            options={[
                                                { label: "All Status", value: "All" },
                                                { label: "Active", value: "Active" },
                                                { label: "Inactive", value: "Inactive" },
                                            ]}
                                        />
                                    </Col>
                                </Row>

                                {/* TABLE */}
                                <div className="table-responsive">
                                    <Table className="align-items-center table-flush" responsive>
                                        <thead className="thead-light">
                                            <tr>
                                                <th>S.No.</th>
                                                <th>Category</th>
                                                <th>Status</th>
                                                <th>Created</th>
                                                <th className="text-right">Actions</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {loading && categories.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-5">
                                                        <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader" />
                                                        <p className="mt-2 text-muted mb-0">Loading categories...</p>
                                                    </td>
                                                </tr>
                                            ) : filteredCategories.length === 0 ? (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-5">
                                                        <p className="text-muted mb-0">
                                                            {searchTerm || statusFilter !== "All"
                                                                ? "No categories found matching your criteria."
                                                                : "No categories available."}
                                                        </p>
                                                    </td>
                                                </tr>
                                            ) : (
                                                filteredCategories.map((item, index) => (
                                                    <tr key={item.uuid || item._id}>
                                                        {/* NUMBER */}
                                                        <td>
                                                            <span className="font-weight-bold text-sm">
                                                                {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                                                            </span>
                                                        </td>

                                                        {/* CATEGORY */}
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="icon icon-shape bg-gradient-primary text-white rounded-circle shadow-sm mr-3" style={{ width: "32px", height: "32px", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "14px" }}>
                                                                    <i className="fas fa-tag" />
                                                                </div>
                                                                <div>
                                                                    <span className="font-weight-bold text-dark d-block">
                                                                        {item.name}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </td>

                                                        {/* STATUS */}
                                                        <td>
                                                            <span
                                                                className={item.isActive ? "badge-active" : "badge-inactive"}
                                                                style={{ cursor: "pointer" }}
                                                                onClick={() => handleToggleStatus(item.uuid || item._id)}
                                                                title="Click to toggle status"
                                                            >
                                                                {item.isActive ? "Active" : "Inactive"}
                                                            </span>
                                                        </td>

                                                        {/* CREATED */}
                                                        <td>
                                                            <span className="text-muted text-sm">
                                                                {item.createdAt && !isNaN(new Date(item.createdAt).getTime())
                                                                    ? new Date(item.createdAt).toLocaleDateString("en-GB", {
                                                                        day: "2-digit",
                                                                        month: "short",
                                                                        year: "numeric",
                                                                    })
                                                                    : "—"}
                                                            </span>
                                                        </td>

                                                        {/* ACTIONS */}
                                                        <td className="text-right">
                                                            <UncontrolledDropdown className="table-action-dropdown">
                                                                <DropdownToggle
                                                                    className="btn-icon-only text-light"
                                                                    color=""
                                                                    role="button"
                                                                    size="sm"
                                                                >
                                                                    <i className="fas fa-ellipsis-v" />
                                                                </DropdownToggle>

                                                                <DropdownMenu right className="shadow border-0">
                                                                    <DropdownItem onClick={() => openEditModal(item)}>
                                                                        <FaEdit className="mr-2 text-primary" />
                                                                        Edit
                                                                    </DropdownItem>

                                                                    <DropdownItem onClick={() => handleToggleStatus(item.uuid || item._id)}>
                                                                        {item.isActive ? (
                                                                            <FaPause className="mr-2 text-warning" />
                                                                        ) : (
                                                                            <FaPlay className="mr-2 text-success" />
                                                                        )}
                                                                        {item.isActive ? "Deactivate" : "Activate"}
                                                                    </DropdownItem>

                                                                    <DropdownItem divider />

                                                                    <DropdownItem
                                                                        className="text-danger"
                                                                        onClick={() => handleDelete(item.uuid || item._id)}
                                                                    >
                                                                        <FaTrash className="mr-2" />
                                                                        Delete
                                                                    </DropdownItem>
                                                                </DropdownMenu>
                                                            </UncontrolledDropdown>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>

                            {/* ================= FOOTER ================= */}
                            {!loading && totalItems > 0 && (
                                <CardFooter className="py-4 border-0">
                                    <CustomPagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        totalItems={totalItems}
                                        itemsPerPage={ITEMS_PER_PAGE}
                                        onPageChange={handlePageChange}
                                        itemName="categories"
                                    />
                                </CardFooter>
                            )}
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* =====================================================
                CREATE / EDIT CATEGORY MODAL
            ===================================================== */}

            <Modal
                isOpen={modalOpen}
                toggle={closeModal}
                centered
                className="category-modal"
            >

                <ModalHeader toggle={closeModal}>

                    <div className="category-modal-header-content">

                        <div className="category-modal-header-icon">
                            <i
                                className={`fas ${editMode
                                    ? "fa-edit"
                                    : "fa-plus"
                                    }`}
                            />
                        </div>

                        <div>
                            <h4 className="mb-0">
                                {editMode
                                    ? "Edit Category"
                                    : "Add New Category"}
                            </h4>

                            <small>
                                {editMode
                                    ? "Update category information"
                                    : "Create a new category"}
                            </small>
                        </div>

                    </div>

                </ModalHeader>

                <Form onSubmit={handleSubmit} noValidate>

                    <ModalBody>

                        <FormGroup className="category-form-group">

                            <Label>
                                Category Name{" "}
                                <span className="required-star">
                                    *
                                </span>
                            </Label>

                            <Input
                                type="text"
                                className={`category-form-input ${touched.name && validationErrors.name ? "is-invalid" : ""
                                    }`}
                                value={formData.name}
                                onChange={(e) =>
                                    handleFieldChange("name", e.target.value)
                                }
                                onBlur={() => handleFieldBlur("name")}
                                placeholder="Enter category name (2-20 characters)"
                                required
                                maxLength={20}
                            />

                            {touched.name && validationErrors.name && (
                                <div className="text-danger small mt-1">
                                    <i className="fas fa-exclamation-circle mr-1" />
                                    {validationErrors.name}
                                </div>
                            )}

                            <small className="category-input-help">
                                Enter a unique name for this category.
                            </small>

                        </FormGroup>

                    </ModalBody>

                    <ModalFooter className="category-modal-footer">

                        <Button
                            className="category-modal-cancel-btn"
                            size="sm"
                            type="button"
                            onClick={closeModal}
                        >
                            <i className="fas fa-times mr-1" />
                            Cancel
                        </Button>

                        <Button
                            className="category-modal-save-btn"
                            size="sm"
                            type="submit"
                            disabled={!isFormValid()}
                        >
                            <i
                                className={`fas ${editMode ? "fa-save" : "fa-plus"
                                    } mr-1`}
                            />
                            {editMode ? "Update Category" : "Create Category"}
                        </Button>
                    </ModalFooter>

                </Form>

            </Modal>
        </>
    );
};

export default Categories;